import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert } from 'components/Alert';
import { Button } from 'components/Button';
import { Input } from 'components/Input';
import { useUnsavedChangesGuard } from 'components/OrganizationScope/context';
import { TagSelector } from 'components/TagSelector';
import { AdCodeBadge } from 'components/AdCodeBadge';
import { Textarea } from 'components/Textarea';
import { pageCardClasses } from 'components/AppLayout/styles';
import { LINE_NAME_MAX_LENGTH, MEMO_MAX_LENGTH } from 'constants/index';
import { routes } from 'constants/routes';
import { useOrgPath } from 'hooks/useOrgPath';
import {
  commonStrings,
  tagManagementStrings,
  userStrings,
  adCodeStrings,
} from 'constants/strings';
import { useAuth } from 'contexts/AuthContext';
import {
  useCurrentUser,
  useLineFriend,
  useTags,
  useUpdateLineFriend,
} from 'hooks';
import { EOrganizationFeature } from 'types/enum';
import {
  canFetchOrganizationFeature,
  isOrganizationFeatureEnabled,
} from 'utils/organizationFeature';
import {
  formatLineFriendBasicId,
  formatLineFriendDateTime,
  getFriendStatusLabel,
} from '../lineFriendView';
import { formatDateTime, mediumLabel } from '../../AdCodes/adCodeDisplay';
import { TestUserSwitch } from '../TestUserSwitch';
import { getUserSurveyAnswers } from 'api/surveys';

const USER_SURVEY_ANSWERS_QUERY_KEY = 'user_survey_answers';

const normalizeOptionalText = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
};

export default function UserEdit() {
  const navigate = useNavigate();
  const orgPathFor = useOrgPath();
  const { userLineId } = useParams();

  const userLineIdNumber = Number(userLineId);
  const hasValidId = Number.isFinite(userLineIdNumber) && userLineIdNumber > 0;

  const { friend, loading, error } = useLineFriend(
    hasValidId ? userLineIdNumber : undefined,
  );

  const [lineName, setLineName] = useState('');
  const [memo, setMemo] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [isTestUser, setIsTestUser] = useState(false);
  const [validationError, setValidationError] = useState<string | undefined>();
  const markSaved = useUnsavedChangesGuard(
    JSON.stringify({ lineName, memo, selectedTagIds, isTestUser }),
  );

  const { user } = useAuth();
  const { data: currentUser } = useCurrentUser();
  const canUseTags = isOrganizationFeatureEnabled(
    currentUser,
    EOrganizationFeature.TAG_MANAGEMENT,
  );
  // アンケートがOFFの組織では回答APIが403になるので取得も表示もしない
  const canUseSurvey = isOrganizationFeatureEnabled(
    currentUser,
    EOrganizationFeature.SURVEY,
  );

  const surveyAnswersQuery = useQuery({
    queryKey: [USER_SURVEY_ANSWERS_QUERY_KEY, user?.uid, userLineIdNumber],
    enabled:
      hasValidId &&
      Boolean(user?.uid) &&
      canFetchOrganizationFeature(currentUser, EOrganizationFeature.SURVEY),
    queryFn: () => getUserSurveyAnswers(userLineIdNumber),
    retry: false,
  });

  const surveyAnswers = surveyAnswersQuery.data ?? [];

  const { tags } = useTags({
    enabled: canFetchOrganizationFeature(currentUser, EOrganizationFeature.TAG_MANAGEMENT),
  });

  const {
    update,
    isSubmitting,
    error: updateError,
  } = useUpdateLineFriend({
    onSuccess: () => {
      markSaved();
      navigate(orgPathFor(routes.USERS), {
        state: { snackbarMessage: commonStrings.actions.updateSuccess },
      });
    },
  });

  useEffect(() => {
    if (friend) {
      setLineName(friend.lineName);
      setMemo(friend.memo ?? '');
      setSelectedTagIds(friend.tags.map((tag) => tag.id));
      setIsTestUser(friend.isTestUser);
      setValidationError(undefined);
      markSaved();
      return;
    }

    setLineName('');
    setMemo('');
    setSelectedTagIds([]);
    setIsTestUser(false);
    setValidationError(undefined);
    markSaved();
  }, [friend, markSaved]);

  const toggleTag = (tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  const isFormDisabled = loading || !friend;
  const notFound = !loading && !error && (!hasValidId || !friend);
  const alertMessage =
    validationError ||
    updateError ||
    error ||
    (notFound ? userStrings.edit.states.notFound : undefined);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isFormDisabled || isSubmitting || !hasValidId) {
      return;
    }

    const normalizedLineName = lineName.trim();
    if (!normalizedLineName) {
      setValidationError(userStrings.edit.errors.required);
      return;
    }

    setValidationError(undefined);
    update(userLineIdNumber, {
      lineName: normalizedLineName,
      memo: normalizeOptionalText(memo),
      // タグ管理がOFFの組織では tagIds を送らない（送信すると既存タグが全解除される）
      ...(canUseTags ? { tagIds: selectedTagIds } : {}),
      isTestUser,
    });
  };
  // LINE名
  const originalLineName =
    friend?.originalLineName ?? userStrings.edit.summary.emptyValue;
  // ベーシックID
  const linkedBasicId =
    formatLineFriendBasicId(friend?.linkedBasicId) ??
    userStrings.table.noLinkedBasicId;

  // 通常 / ブロック
  const friendStatus = friend
    ? getFriendStatusLabel(friend.friendStatus)
    : userStrings.edit.summary.emptyValue;
  // 最終アクセス
  const lastAccessAt =
    formatLineFriendDateTime(friend?.lastAccessAt) ??
    userStrings.edit.summary.emptyValue;

  // 登録日時
  const friendRegisteredAt =
    formatLineFriendDateTime(friend?.friendRegisteredAt) ??
    userStrings.edit.summary.emptyValue;

  return (
    <>
      <section className={`mx-auto w-full max-w-[980px] ${pageCardClasses.large}`}>
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid gap-2">
            <span className="text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-muted">
              {userStrings.edit.kicker}
            </span>
            <h1 className="text-2xl font-display text-ink">
              {userStrings.edit.title}
            </h1>
            <p className="text-sm text-muted">{userStrings.edit.description}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => navigate(orgPathFor(routes.USERS))}
            >
              {commonStrings.actions.backToList}
            </Button>
          </div>
        </header>

        {alertMessage && (
          <div className="mt-6">
            <Alert>{alertMessage}</Alert>
          </div>
        )}

        <div className="mt-6 grid gap-4">
          <section className="grid gap-3 rounded-2xl border border-border/70 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-ink">
              {userStrings.edit.summary.title}
            </h2>
            <dl className="grid gap-3 sm:grid-cols-2">
              <div>
                <dt className="text-[11px] font-semibold text-muted">
                  {userStrings.edit.summary.labels.id}
                </dt>
                <dd className="mt-1 text-sm text-ink">
                  {friend?.id ?? userStrings.edit.summary.emptyValue}
                </dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold text-muted">
                  {userStrings.edit.summary.labels.originalLineName}
                </dt>
                <dd className="mt-1 text-sm text-ink">{originalLineName}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold text-muted">
                  {userStrings.edit.summary.labels.linkedBasicId}
                </dt>
                <dd className="mt-1 text-sm text-ink">{linkedBasicId}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold text-muted">
                  {userStrings.edit.summary.labels.friendStatus}
                </dt>
                <dd className="mt-1 text-sm text-ink">{friendStatus}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold text-muted">
                  {userStrings.edit.summary.labels.lastAccessAt}
                </dt>
                <dd className="mt-1 text-sm text-ink">{lastAccessAt}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-semibold text-muted">
                  {userStrings.edit.summary.labels.friendRegisteredAt}
                </dt>
                <dd className="mt-1 text-sm text-ink">{friendRegisteredAt}</dd>
              </div>
            </dl>
          </section>

          <form
            className="grid gap-4 rounded-2xl border border-border/70 bg-white p-5 shadow-sm"
            onSubmit={handleSubmit}
          >
            <div className="grid gap-1">
              <h2 className="text-sm font-semibold text-ink">
                {userStrings.edit.form.title}
              </h2>
              <p className="text-xs text-muted">
                {userStrings.edit.form.description}
              </p>
            </div>

            <label className="grid gap-2 text-xs font-semibold text-ink">
              <span>
                {userStrings.edit.form.fields.lineName.label}
                <span className="ml-1 text-danger">
                  {commonStrings.required}
                </span>
              </span>
              <Input
                name="lineName"
                placeholder={userStrings.edit.form.fields.lineName.placeholder}
                autoComplete="off"
                required
                maxLength={LINE_NAME_MAX_LENGTH}
                disabled={isFormDisabled || isSubmitting}
                value={lineName}
                onChange={(event) => setLineName(event.target.value)}
              />
            </label>

            <label className="grid gap-2 text-xs font-semibold text-ink">
              <span>{userStrings.edit.form.fields.memo.label}</span>
              <Textarea
                name="memo"
                placeholder={userStrings.edit.form.fields.memo.placeholder}
                maxLength={MEMO_MAX_LENGTH}
                disabled={isFormDisabled || isSubmitting}
                value={memo}
                onChange={(event) => setMemo(event.target.value)}
              />
              <span className="text-[11px] font-medium text-muted">
                {userStrings.edit.form.fields.memo.helper} ({memo.length}/
                {MEMO_MAX_LENGTH})
              </span>
            </label>

            <div className="grid gap-2 text-xs font-semibold text-ink">
              <span>{userStrings.filters.adCode}</span>
              {friend?.adCode ? (
                <div className="grid gap-1 rounded-2xl border border-border/60 p-3 font-medium">
                  <AdCodeBadge
                    name={friend.adCode.name}
                    confidence={friend.adCode.confidence}
                  />
                  <span className="text-[11px] text-muted">
                    {mediumLabel(friend.adCode.medium)} ・{' '}
                    {adCodeStrings.method[friend.adCode.method]} ・{' '}
                    {formatDateTime(friend.adCode.attributedAt)}
                  </span>
                </div>
              ) : (
                <p className="text-[11px] font-medium text-muted">
                  {userStrings.filters.adCodeNone}
                </p>
              )}
            </div>

            {canUseTags && (
              <div className="grid gap-2 text-xs font-semibold text-ink">
                <span>{tagManagementStrings.editForFriend.title}</span>
                {tags.length === 0 ? (
                  <p className="text-[11px] font-medium text-muted">
                    {tagManagementStrings.editForFriend.empty}
                  </p>
                ) : (
                  <TagSelector
                    tags={tags}
                    selectedTagIds={selectedTagIds}
                    onToggle={toggleTag}
                    disabled={isFormDisabled || isSubmitting}
                  />
                )}
              </div>
            )}

            {canUseSurvey && (
              <div className="grid gap-2 text-xs font-semibold text-ink">
                <span>アンケート回答</span>
                {hasValidId && surveyAnswersQuery.isPending ? (
                  <p
                    className="text-[11px] font-medium text-muted"
                    role="status"
                  >
                    回答履歴を読み込み中です
                  </p>
                ) : surveyAnswersQuery.isError ? (
                  <div className="grid justify-items-start gap-2">
                    <Alert>回答履歴を読み込めませんでした</Alert>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      loading={surveyAnswersQuery.isFetching}
                      onClick={() => {
                        void surveyAnswersQuery.refetch();
                      }}
                    >
                      再試行
                    </Button>
                  </div>
                ) : surveyAnswers.length === 0 ? (
                  <p className="text-[11px] font-medium text-muted">
                    回答履歴はありません
                  </p>
                ) : (
                  <div className="space-y-3">
                    {surveyAnswers.map((survey) => (
                      <div key={survey.responseId} className="rounded border p-3">
                        <p className="font-semibold">{survey.surveyName}</p>
                        {survey.answers.map((answer) => (
                          <div key={answer.questionId} className="mt-2">
                            <p>{answer.questionTitle}</p>
                            <p className="text-muted">{answer.value}</p>
                            <p className="text-[11px] font-medium text-muted">
                              回答日時:{' '}
                              {formatLineFriendDateTime(answer.answeredAt) ??
                                userStrings.edit.summary.emptyValue}
                            </p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex items-start gap-3">
              <TestUserSwitch
                checked={isTestUser}
                disabled={isFormDisabled || isSubmitting}
                ariaLabel={userStrings.edit.form.fields.isTestUser.label}
                onChange={setIsTestUser}
              />
              <span className="grid gap-0.5">
                <span className="text-xs font-semibold text-ink">
                  {userStrings.edit.form.fields.isTestUser.label}
                </span>
                <span className="text-[11px] font-medium text-muted">
                  {userStrings.edit.form.fields.isTestUser.helper}
                </span>
              </span>
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                loading={isSubmitting}
                disabled={isFormDisabled}
              >
                {commonStrings.actions.update}
              </Button>
            </div>
          </form>
        </div>
      </section>

    </>
  );
}
